import React, { useCallback, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import cs from "classnames";
import NukaCarousel from "nuka-carousel";
import { CarouselButton } from "@lululemon/ui-library";
import { useUniDirectionalScroll } from "../../hooks/useUniDirectionalScroll";

import styles from "./carousel-v3.module.scss";

export const MOBILE_GRID_GAP = 10;
export const TABLET_GRID_GAP = 15;
export const DESKTOP_GRID_GAP = 25;

function CarouselV3(props) {
  const {
    ariaLabelledbyId,
    cellSpacing,
    children,
    className,
    defaultControlsConfig,
    dragging,
    frameAriaLabel,
    handleLeftArrowClick,
    handleRightArrowClick,
    hideArrowsOnMobile,
    renderBottomCenterControls,
    renderCenterLeftControls,
    renderCenterRightControls,
    renderTopLeftControls,
    renderTopRightControls,
    slidesToShow,
    swiping,
    verticalCenterRef,
    withoutControls,
    wrapAround,
    deviceType,
    ...rest
  } = props;
  const [arrowTopOffset, setArrowTopOffset] = useState();
  const carouselRef = useRef();
  const { isHorizontalScroll } = useUniDirectionalScroll(carouselRef);

  const defaultTopLeftControls = ({ previousDisabled, previousSlide }) => {
    return (
      !disableCarousel && (
        <CarouselButton
          className={cs(styles.prevButton, {
            [styles.hideArrowsOnMobile]: hideArrowsOnMobile,
            [styles.hideArrows]:
              previousDisabled || arrowTopOffset === undefined
          })}
          direction="left"
          onClick={() => {
            previousSlide();
            handleLeftArrowClick && handleLeftArrowClick();
          }}
          style={{
            top:
              arrowTopOffset !== undefined ? `${arrowTopOffset}px` : undefined
          }}
        />
      )
    );
  };

  const defaultTopRightControls = ({
    nextDisabled,
    nextSlide,
    currentSlide,
    slideCount
  }) => {
    // with scrollMode "remainder", the nextDisabled prop doesn't work properly due to currentSlide being a fraction
    const buttonDisabled =
      nextDisabled ||
      (!wrapAround && slideCount - Math.ceil(currentSlide) < slidesToShow);
    return (
      !disableCarousel && (
        <CarouselButton
          className={cs(styles.nextButton, {
            [styles.hideArrowsOnMobile]: hideArrowsOnMobile,
            [styles.hideArrows]: buttonDisabled || arrowTopOffset === undefined
          })}
          direction="right"
          onClick={() => {
            nextSlide();
            handleRightArrowClick && handleRightArrowClick();
          }}
          style={{
            top:
              arrowTopOffset !== undefined ? `${arrowTopOffset}px` : undefined
          }}
        />
      )
    );
  };

  const setArrowTopOffsetFromRef = useCallback(() => {
    const imageHeight = Math.round(
      verticalCenterRef?.getBoundingClientRect()?.height ?? 0
    );

    if (imageHeight > 0) {
      const newArrowTopOffset = imageHeight / 2;
      if (newArrowTopOffset !== arrowTopOffset) {
        setArrowTopOffset(newArrowTopOffset);
      }
    }
  }, [arrowTopOffset, verticalCenterRef]);

  useEffect(() => {
    setArrowTopOffsetFromRef();
  }, [setArrowTopOffsetFromRef, verticalCenterRef]);

  const handleResize = useCallback(() => {
    setArrowTopOffsetFromRef();
  }, [setArrowTopOffsetFromRef]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  let defaultCellSpacing;

  if (deviceType.isMobile) {
    defaultCellSpacing = MOBILE_GRID_GAP;
  } else if (deviceType.isTablet) {
    defaultCellSpacing = TABLET_GRID_GAP;
  } else {
    defaultCellSpacing = DESKTOP_GRID_GAP;
  }

  const disableCarousel =
    React.Children.toArray(children).length <= slidesToShow;

  if (!children) return null;

  return (
    <div
      aria-roledescription="carousel"
      role="group"
      aria-labelledby={ariaLabelledbyId}
    >
      <NukaCarousel
        cellSpacing={
          typeof cellSpacing !== "undefined" ? cellSpacing : defaultCellSpacing
        }
        className={cs({
          [styles.slidesWrapper]: renderBottomCenterControls !== false,
          [styles.nonCarouselContainer]: disableCarousel,
          [className]: !!className
        })}
        defaultControlsConfig={{
          pagingDotsContainerClassName: styles.dotsContainer,
          pagingDotsClassName: styles.dot,
          ...defaultControlsConfig
        }}
        dragging={
          typeof dragging !== "undefined"
            ? dragging
            : (isHorizontalScroll || isHorizontalScroll === null) &&
              !disableCarousel
        }
        frameAriaLabel={frameAriaLabel}
        innerRef={carouselRef}
        renderBottomCenterControls={renderBottomCenterControls}
        renderCenterLeftControls={renderCenterLeftControls || null} // null to prevent duplicate control
        renderCenterRightControls={renderCenterRightControls || null} // null to prevent duplicate control
        renderTopLeftControls={renderTopLeftControls || defaultTopLeftControls}
        renderTopRightControls={
          renderTopRightControls || defaultTopRightControls
        }
        swiping={
          typeof swiping !== "undefined"
            ? swiping
            : isHorizontalScroll || isHorizontalScroll === null
        }
        withoutControls={
          typeof withoutControls !== "undefined"
            ? withoutControls
            : disableCarousel
        }
        wrapAround={
          typeof wrapAround !== "undefined" ? wrapAround : !disableCarousel
        }
        slidesToShow={slidesToShow}
        {...rest}
      >
        {children}
      </NukaCarousel>
    </div>
  );
}

CarouselV3.defaultProps = {
  ariaLabelledbyId: undefined,
  cellSpacing: undefined,
  className: undefined,
  defaultControlsConfig: undefined,
  dragging: undefined,
  frameAriaLabel: "Carousel Slides",
  handleLeftArrowClick: undefined,
  handleRightArrowClick: undefined,
  hideArrowsOnMobile: true,
  renderBottomCenterControls: undefined,
  renderCenterLeftControls: undefined,
  renderCenterRightControls: undefined,
  renderTopLeftControls: undefined,
  renderTopRightControls: undefined,
  swiping: undefined,
  verticalCenterRef: undefined,
  withoutControls: undefined,
  wrapAround: undefined
};

CarouselV3.propTypes = {
  ariaLabelledbyId: PropTypes.string,
  cellSpacing: PropTypes.number,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  className: PropTypes.string,
  defaultControlsConfig: PropTypes.object,
  dragging: PropTypes.bool,
  frameAriaLabel: PropTypes.string,
  hideArrowsOnMobile: PropTypes.bool,
  renderBottomCenterControls: PropTypes.bool,
  renderCenterLeftControls: PropTypes.func,
  renderCenterRightControls: PropTypes.func,
  renderTopLeftControls: PropTypes.func,
  renderTopRightControls: PropTypes.func,
  slidesToShow: PropTypes.number.isRequired,
  swiping: PropTypes.bool,
  withoutControls: PropTypes.bool,
  wrapAround: PropTypes.bool,
  verticalCenterRef: PropTypes.oneOfType([
    PropTypes.func,
    // eslint-disable-next-line react/forbid-prop-types
    PropTypes.shape({ current: PropTypes.any })
  ]),
  handleLeftArrowClick: PropTypes.func,
  handleRightArrowClick: PropTypes.func,
  deviceType: PropTypes.shape({
    isMobile: PropTypes.bool,
    isTablet: PropTypes.bool,
    isDesktop: PropTypes.bool
  })
};

export default CarouselV3;
